import { Link } from 'react-router-dom'
import { useWallet } from '../contexts/WalletContext'
import { Upload, Users, Shield, Zap, ArrowRight } from 'lucide-react'
import HarmonyHubLogo from '../components/HarmonyHubLogo'

export default function Home() {
  const { isConnected } = useWallet()

  return (
    <>
        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px) translateX(0px) rotate(0deg); }
            25% { transform: translateY(-20px) translateX(10px) rotate(90deg); }
            50% { transform: translateY(-10px) translateX(-5px) rotate(180deg); }
            75% { transform: translateY(-15px) translateX(8px) rotate(270deg); }
          }
          
          @keyframes float-gentle {
            0%, 100% { 
              transform: translateY(0px) scale(1);
              filter: drop-shadow(0 0 15px rgba(138, 43, 226, 0.3));
            }
            50% { 
              transform: translateY(-8px) scale(1.02);
              filter: drop-shadow(0 0 25px rgba(138, 43, 226, 0.5));
            }
          }
          
          @keyframes glisten {
            0%, 100% { 
              background-position: -200% center;
              opacity: 0.6;
            }
            50% { 
              background-position: 200% center;
              opacity: 1;
            }
          }
          
          @keyframes sparkle {
            0%, 100% { 
              opacity: 0;
              transform: scale(0.8) rotate(0deg);
            }
            25% { 
              opacity: 1;
              transform: scale(1.2) rotate(90deg);
            }
            50% { 
              opacity: 0.8;
              transform: scale(1) rotate(180deg);
            }
            75% { 
              opacity: 1;
              transform: scale(1.1) rotate(270deg);
            }
          }
          
          @keyframes pulse-glow {
            0%, 100% { 
              box-shadow: 0 0 20px rgba(212, 175, 55, 0.4);
              transform: scale(1);
            }
            50% { 
              box-shadow: 0 0 40px rgba(212, 175, 55, 0.8);
              transform: scale(1.05);
            }
          }
        `}</style>
      <div className="min-h-screen">
      {/* Hero Section */}
      <section className="text-white relative overflow-hidden"
               style={{
                 background: `
                   linear-gradient(135deg, #1F2937 0%, #4B5563 25%, #6B7280 50%, #4B5563 75%, #1F2937 100%),
                   radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.15) 0%, transparent 50%),
                   radial-gradient(circle at 80% 70%, rgba(138, 43, 226, 0.15) 0%, transparent 50%),
                   radial-gradient(circle at 50% 20%, rgba(59, 130, 246, 0.15) 0%, transparent 50%)
                 `,
                 backgroundSize: '100% 100%, 800px 800px, 900px 900px, 700px 700px'
               }}>
        {/* Metallic shine effect */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/12 to-transparent transform -skew-y-1 animate-pulse"></div>
        </div>

        {/* Advanced 3D Audio Visualizer */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Floating 3D Music Notes with Advanced Animations */}
          <div className="absolute top-1/4 left-1/4 w-16 h-16 opacity-30">
            <div className="w-full h-full bg-gradient-to-br from-gold-400 to-gold-600 rounded-full animate-spin" style={{animationDuration: '8s'}}>
              <div className="w-8 h-8 bg-white rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-gold-600">
                  <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                </svg>
              </div>
            </div>
          </div>
          
          <div className="absolute top-1/3 right-1/4 w-12 h-12 opacity-25">
            <div className="w-full h-full bg-gradient-to-br from-purple-400 to-purple-600 rounded-full animate-pulse" style={{animationDuration: '3s'}}>
              <div className="w-6 h-6 bg-white rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3 text-purple-600">
                  <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                </svg>
              </div>
            </div>
          </div>

          <div className="absolute bottom-1/3 left-1/3 w-20 h-20 opacity-20">
            <div className="w-full h-full bg-gradient-to-br from-violet-400 to-violet-600 rounded-full animate-spin" style={{animationDuration: '12s', animationDirection: 'reverse'}}>
              <div className="w-10 h-10 bg-white rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-violet-600">
                  <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                </svg>
              </div>
            </div>
          </div>

          <div className="absolute top-2/3 right-1/3 w-14 h-14 opacity-30">
            <div className="w-full h-full bg-gradient-to-br from-gold-400 to-gold-600 rounded-full animate-bounce" style={{animationDuration: '4s'}}>
              <div className="w-7 h-7 bg-white rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-gold-600">
                  <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Blockchain Network Visualization */}
        <div className="absolute inset-0 opacity-20">
          {/* Animated Connection Lines */}
          <svg className="w-full h-full absolute inset-0" style={{zIndex: 1}}>
            <defs>
              <linearGradient id="connectionGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgba(212, 175, 55, 0.6)" />
                <stop offset="50%" stopColor="rgba(138, 43, 226, 0.6)" />
                <stop offset="100%" stopColor="rgba(59, 130, 246, 0.6)" />
              </linearGradient>
            </defs>
            <path d="M 25% 25% Q 50% 10% 75% 25%" stroke="url(#connectionGradient2)" strokeWidth="2" fill="none" opacity="0.6">
              <animate attributeName="opacity" values="0.2;0.8;0.2" dur="3s" repeatCount="indefinite"/>
            </path>
            <path d="M 75% 25% Q 90% 50% 75% 75%" stroke="url(#connectionGradient2)" strokeWidth="2" fill="none" opacity="0.6">
              <animate attributeName="opacity" values="0.8;0.2;0.8" dur="3s" repeatCount="indefinite"/>
            </path>
            <path d="M 75% 75% Q 50% 90% 25% 75%" stroke="url(#connectionGradient2)" strokeWidth="2" fill="none" opacity="0.6">
              <animate attributeName="opacity" values="0.2;0.8;0.2" dur="3s" repeatCount="indefinite"/>
            </path>
            <path d="M 25% 75% Q 10% 50% 25% 25%" stroke="url(#connectionGradient2)" strokeWidth="2" fill="none" opacity="0.6">
              <animate attributeName="opacity" values="0.8;0.2;0.8" dur="3s" repeatCount="indefinite"/>
            </path>
          </svg>
          
          {/* 3D Animated Nodes */}
          <div className="absolute top-1/4 left-1/4 w-4 h-4 bg-gradient-to-br from-gold-400 to-gold-600 rounded-full animate-ping shadow-lg shadow-gold-400/50" style={{animationDuration: '2s'}}></div>
          <div className="absolute top-1/2 right-1/4 w-4 h-4 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full animate-ping shadow-lg shadow-purple-400/50" style={{animationDelay: '0.5s', animationDuration: '2s'}}></div>
          <div className="absolute bottom-1/4 left-1/2 w-4 h-4 bg-gradient-to-br from-violet-400 to-violet-600 rounded-full animate-ping shadow-lg shadow-violet-400/50" style={{animationDelay: '1s', animationDuration: '2s'}}></div>
          <div className="absolute top-3/4 right-1/3 w-4 h-4 bg-gradient-to-br from-gold-400 to-gold-600 rounded-full animate-ping shadow-lg shadow-gold-400/50" style={{animationDelay: '1.5s', animationDuration: '2s'}}></div>
          
          {/* Central Hub Node */}
          <div className="absolute top-1/2 left-1/2 w-6 h-6 bg-gradient-to-br from-white to-gray-200 rounded-full transform -translate-x-1/2 -translate-y-1/2 shadow-2xl shadow-gold-400/30 animate-pulse" style={{animationDuration: '4s'}}></div>
        </div>

        {/* Floating Data Particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="absolute w-2 h-2 bg-gradient-to-r from-gold-400 to-purple-400 rounded-full opacity-30"
                 style={{
                   left: `${Math.random() * 100}%`,
                   top: `${Math.random() * 100}%`,
                   animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
                   animationDelay: `${Math.random() * 2}s`
                 }}></div>
          ))}
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative">
          <div className="text-center">
            <div className="relative mb-12">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-400/10 to-violet-400/10 blur-2xl animate-pulse"></div>
              
              {/* Glistening overlay */}
              <div className="absolute inset-0 opacity-40 pointer-events-none">
                <div className="absolute top-1/4 left-1/4 w-4 h-4 bg-white rounded-full animate-sparkle" style={{animationDelay: '0s', animationDuration: '3s'}}></div>
                <div className="absolute top-1/3 right-1/4 w-3 h-3 bg-gold-400 rounded-full animate-sparkle" style={{animationDelay: '1s', animationDuration: '4s'}}></div>
                <div className="absolute bottom-1/3 left-1/3 w-3.5 h-3.5 bg-white rounded-full animate-sparkle" style={{animationDelay: '2s', animationDuration: '3.5s'}}></div>
                <div className="absolute bottom-1/4 right-1/3 w-3 h-3 bg-gold-400 rounded-full animate-sparkle" style={{animationDelay: '0.5s', animationDuration: '4.5s'}}></div>
                <div className="absolute top-1/2 left-1/2 w-2.5 h-2.5 bg-white rounded-full animate-sparkle" style={{animationDelay: '1.5s', animationDuration: '3s'}}></div>
                <div className="absolute top-1/5 right-1/5 w-2 h-2 bg-gold-400 rounded-full animate-sparkle" style={{animationDelay: '2.5s', animationDuration: '4.2s'}}></div>
                <div className="absolute bottom-1/5 left-1/5 w-2.5 h-2.5 bg-white rounded-full animate-sparkle" style={{animationDelay: '0.8s', animationDuration: '3.8s'}}></div>
              </div>
              
              <h1 className="relative text-7xl md:text-9xl lg:text-[10rem] font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-300 via-violet-400 to-purple-500" 
                  style={{ 
                    fontFamily: 'Georgia, serif',
                    textShadow: `
                      0 0 20px rgba(138, 43, 226, 0.4),
                      0 0 40px rgba(138, 43, 226, 0.3),
                      2px 2px 4px rgba(0, 0, 0, 0.2)
                    `,
                    filter: 'drop-shadow(0 0 15px rgba(138, 43, 226, 0.3))',
                    transform: 'translateY(0px)',
                    backgroundImage: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.8) 50%, transparent 70%)',
                    backgroundSize: '200% 100%',
                    backgroundPosition: '-200% center',
                    animation: 'float-gentle 6s ease-in-out infinite, glisten 4s ease-in-out infinite'
                  }}>
                Harmony Hub
              </h1>
            </div>
            <p className="text-lg md:text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
              Create, share, and monetize your content on the most secure and decentralized platform. 
              Powered by Sui blockchain, Walrus storage, and Seal encryption.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              {isConnected ? (
                <>
                  <Link to="/create-profile" className="group relative px-8 py-4 bg-gradient-to-r from-gold-500 to-gold-600 text-black font-bold text-lg rounded-2xl hover:from-gold-600 hover:to-gold-700 transition-all duration-300 shadow-2xl shadow-gold-500/40 hover:shadow-gold-500/60 transform hover:scale-105 flex items-center gap-3">
                    <div className="w-6 h-6 bg-black/20 rounded-full flex items-center justify-center">
                      👤
                    </div>
                    Manage Profile
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                  </Link>
                  <Link to="/website-builder" className="group relative px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold text-lg rounded-2xl hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-2xl shadow-purple-500/40 hover:shadow-purple-500/60 transform hover:scale-105 flex items-center gap-3">
                    <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                      🌐
                    </div>
                    Build Website
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                  </Link>
                  <Link to="/upload" className="group relative px-8 py-4 bg-gradient-to-r from-violet-600 to-pink-600 text-white font-bold text-lg rounded-2xl hover:from-violet-700 hover:to-pink-700 transition-all duration-300 shadow-2xl shadow-violet-500/40 hover:shadow-violet-500/60 transform hover:scale-105 flex items-center gap-3">
                    <Upload className="w-5 h-5" />
                    Upload Content
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                  </Link>
                </>
              ) : (
                <Link to="/create-profile" className="group relative px-10 py-5 bg-gradient-to-r from-gold-500 to-gold-600 text-black font-bold text-xl rounded-2xl hover:from-gold-600 hover:to-gold-700 transition-all duration-300 shadow-2xl shadow-gold-500/40 hover:shadow-gold-500/60 transform hover:scale-105 flex items-center gap-3">
                  <div className="w-7 h-7 bg-black/20 rounded-full flex items-center justify-center text-lg">
                    ✨
                  </div>
                  Join Harmony Hub
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                </Link>
              )}
              <Link to="/u/demo" className="group relative px-8 py-4 bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white font-semibold text-lg rounded-2xl hover:bg-white/20 hover:border-white/50 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105 flex items-center gap-3">
                <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                  <ArrowRight className="w-4 h-4" />
                </div>
                View Demo Profile
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 relative overflow-hidden"
               style={{
                 background: `
                   linear-gradient(135deg, #1F2937 0%, #374151 25%, #4B5563 50%, #374151 75%, #1F2937 100%),
                   radial-gradient(circle at 30% 40%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
                   radial-gradient(circle at 70% 60%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
                 `,
                 backgroundSize: '100% 100%, 800px 800px, 900px 900px'
               }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="relative mb-6">
              <div className="absolute inset-0 bg-gradient-to-r from-gold-400/5 to-purple-400/5 blur-2xl"></div>
              <h2 className="relative text-4xl md:text-5xl font-bold text-white" 
                  style={{ 
                    fontFamily: 'Georgia, serif',
                    textShadow: `
                      0 0 15px rgba(212, 175, 55, 0.3),
                      0 0 30px rgba(212, 175, 55, 0.2),
                      1px 1px 2px rgba(0, 0, 0, 0.1)
                    `,
                    filter: 'drop-shadow(0 0 10px rgba(212, 175, 55, 0.2))'
                  }}>
                Why Choose Harmony Hub?
              </h2>
            </div>
            <p className="text-xl text-purple-100 max-w-2xl mx-auto">
              Built on cutting-edge blockchain technology for creators who value privacy, security, and true ownership.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-8 rounded-lg hover:shadow-lg transition-shadow border border-white/20 bg-white/5 backdrop-blur-sm">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500/20 to-purple-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">End-to-End Encryption</h3>
              <p className="text-purple-100">
                Your content is protected with Seal encryption, ensuring only you and your subscribers can access it.
              </p>
            </div>

            <div className="text-center p-8 rounded-lg hover:shadow-lg transition-shadow border border-white/20 bg-white/5 backdrop-blur-sm">
              <div className="w-16 h-16 bg-gradient-to-br from-gold-500/20 to-gold-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl text-gold-400">🌐</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Website Builder</h3>
              <p className="text-purple-100">
                Create professional websites with drag-and-drop builder, hosted on decentralized Walrus storage.
              </p>
            </div>

            <div className="text-center p-8 rounded-lg hover:shadow-lg transition-shadow border border-white/20 bg-white/5 backdrop-blur-sm">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Zap className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Lightning Fast</h3>
              <p className="text-purple-100">
                Built on Sui blockchain for instant transactions and Walrus for decentralized, high-speed storage.
              </p>
            </div>

            <div className="text-center p-8 rounded-lg hover:shadow-lg transition-shadow border border-white/20 bg-white/5 backdrop-blur-sm">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500/20 to-green-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Creator Economy</h3>
              <p className="text-purple-100">
                Multiple monetization options including subscriptions, tips, and pay-per-view content.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 relative overflow-hidden"
               style={{
                 background: `
                   linear-gradient(135deg, #374151 0%, #4B5563 25%, #6B7280 50%, #4B5563 75%, #374151 100%),
                   radial-gradient(circle at 40% 30%, rgba(212, 175, 55, 0.08) 0%, transparent 50%),
                   radial-gradient(circle at 60% 70%, rgba(138, 43, 226, 0.08) 0%, transparent 50%)
                 `,
                 backgroundSize: '100% 100%, 700px 700px, 800px 800px'
               }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="relative mb-6">
              <div className="absolute inset-0 bg-gradient-to-r from-gold-400/5 to-purple-400/5 blur-2xl"></div>
              <h2 className="relative text-4xl md:text-5xl font-bold text-white" 
                  style={{ 
                    fontFamily: 'Georgia, serif',
                    textShadow: `
                      0 0 15px rgba(212, 175, 55, 0.3),
                      0 0 30px rgba(212, 175, 55, 0.2),
                      1px 1px 2px rgba(0, 0, 0, 0.1)
                    `,
                    filter: 'drop-shadow(0 0 10px rgba(212, 175, 55, 0.2))'
                  }}>
                How It Works
              </h2>
            </div>
            <p className="text-xl text-purple-100 max-w-2xl mx-auto">
              Get started in three simple steps and join the decentralized creator economy.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-gold-500 to-gold-600 text-black-950 rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-bold shadow-gold">
                1
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Connect Wallet</h3>
              <p className="text-purple-100">
                Connect your Sui wallet to access the platform and manage your digital identity.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-bold">
                2
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Create Profile</h3>
              <p className="text-purple-100">
                Set up your creator profile with custom handle, bio, and social links.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-bold">
                3
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Start Creating</h3>
              <p className="text-purple-100">
                Upload content, set pricing, and start building your subscriber base.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 text-white relative overflow-hidden"
               style={{
                 background: `
                   linear-gradient(135deg, #D4AF37 0%, #7C3AED 25%, #3B82F6 50%, #7C3AED 75%, #D4AF37 100%),
                   radial-gradient(circle at 30% 40%, rgba(255, 255, 255, 0.2) 0%, transparent 50%),
                   radial-gradient(circle at 70% 60%, rgba(212, 175, 55, 0.2) 0%, transparent 50%)
                 `,
                 backgroundSize: '100% 100%, 1000px 1000px, 800px 800px'
               }}>
        {/* Metallic shine effect */}
        <div className="absolute inset-0 opacity-25">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-y-1 animate-pulse"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative">
          <div className="relative mb-8">
            <div className="absolute inset-0 bg-gradient-to-r from-gold-400/10 to-purple-400/10 blur-2xl animate-pulse"></div>
            <h2 className="relative text-4xl md:text-6xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>
              Ready to Join <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-300 via-gold-400 to-gold-500"
                                  style={{
                                    textShadow: `
                                      0 0 20px rgba(212, 175, 55, 0.4),
                                      0 0 40px rgba(212, 175, 55, 0.3),
                                      2px 2px 4px rgba(0, 0, 0, 0.2)
                                    `,
                                    filter: 'drop-shadow(0 0 15px rgba(212, 175, 55, 0.3))'
                                  }}>Harmony Hub</span>?
            </h2>
          </div>
          <p className="text-xl text-purple-100 mb-8">
            Start your journey as a decentralized creator today. No intermediaries, no censorship, just pure creative freedom.
          </p>
          {isConnected ? (
            <Link to="/upload" className="btn-primary bg-white text-purple-600 hover:bg-gray-100 text-lg px-8 py-4 shadow-lg">
              <Upload className="w-5 h-5 mr-2" />
              Upload Your First Content
            </Link>
          ) : (
            <Link to="/create-profile" className="btn-primary bg-white text-purple-600 hover:bg-gray-100 text-lg px-8 py-4 shadow-lg">
              Get Started Now
            </Link>
          )}
        </div>
      </section>
      </div>
    </>
  )
}
